import { useState, useEffect } from "react";
// Fallback bandwidth test for Safari and other browsers without Network API
async function measureBandwidth() {
  try {
    const currentOrigin = window.location.origin;

    // Test URLs - prioritize same-origin, then fallback to external
    const testUrls = [`${currentOrigin}/favicon.ico`,
    // Same origin favicon
    `${currentOrigin}/`,
    // Home page (cache busted)
    "https://www.google.com/favicon.ico" // ~1.1KB
    ];
    let totalBandwidth = 0;
    let successfulTests = 0;
    for (const url of testUrls) {
      try {
        const startTime = performance.now();
        const response = await fetch(url + "?t=" + Date.now(), {
          method: "GET",
          cache: "no-cache",
          mode: url.startsWith(currentOrigin) ? "cors" : "no-cors"
        });
        const endTime = performance.now();
        const duration = (endTime - startTime) / 1000; // Convert to seconds

        if (duration > 0) {
          // Get actual file size from response
          let fileSizeKB = 1; // Default fallback

          try {
            // Try to get size from Content-Length header
            const contentLength = response.headers.get("content-length");
            if (contentLength) {
              fileSizeKB = parseInt(contentLength) / 1024; // Convert bytes to KB
            } else if (url.startsWith(currentOrigin)) {
              // For same-origin requests, we can read the response body
              const blob = await response.blob();
              fileSizeKB = blob.size / 1024;
            }
          } catch {
            // Use URL-specific estimates if headers fail
            if (url.includes("favicon.ico")) fileSizeKB = 2;else if (url === `${currentOrigin}/`) fileSizeKB = 5;else fileSizeKB = 1;
          }
          const bandwidthKbps = fileSizeKB / duration;
          const bandwidthMbps = bandwidthKbps * 8 / 1000; // Convert to Mbps

          console.log(`Network test: ${url}`);
          console.log(`  Size: ${fileSizeKB.toFixed(2)} KB`);
          console.log(`  Duration: ${(duration * 1000).toFixed(1)} ms`);
          console.log(`  Bandwidth: ${bandwidthMbps.toFixed(2)} Mbps`);
          totalBandwidth += bandwidthMbps;
          successfulTests++;
        }
      } catch {
        // Continue to next URL if this one fails
        continue;
      }
    }
    if (successfulTests === 0) {
      // If all tests fail, try a simple timing test
      const startTime = performance.now();
      await new Promise(resolve => setTimeout(resolve, 100));
      const endTime = performance.now();

      // If timing is very slow, assume slow connection
      return endTime - startTime > 150 ? 0.5 : 2;
    }
    const averageBandwidth = totalBandwidth / successfulTests;
    console.log(`Average bandwidth: ${averageBandwidth.toFixed(2)} Mbps from ${successfulTests} tests`);
    return averageBandwidth;
  } catch {
    return 1; // Default fallback
  }
}

// Detect if user is on mobile device
function isMobileDevice() {
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// Get connection type based on user agent and other heuristics
function getConnectionTypeFromUA() {
  // const userAgent = navigator.userAgent.toLowerCase()

  // Check for mobile indicators
  if (isMobileDevice()) {
    return "cellular";
  }

  // Default to unknown for desktop without better detection
  return "unknown";
}
export function useNetworkSpeed() {
  const [networkInfo, setNetworkInfo] = useState({
    speed: "medium",
    connectionType: "unknown",
    effectiveBandwidth: 0,
    saveData: false
  });
  useEffect(() => {
    const updateNetworkInfo = async () => {
      // Check for offline status first
      if (!navigator.onLine) {
        setNetworkInfo(prev => ({
          ...prev,
          speed: "offline"
        }));
        return;
      }

      // Check if Network Information API is available
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      let speed = "medium";
      let connectionType = "unknown";
      let effectiveBandwidth = 0;
      let saveData = false;
      if (connection) {
        // Get effective bandwidth (in Mbps)
        effectiveBandwidth = connection.downlink || 0;
        saveData = connection.saveData || false;

        // Determine connection type
        const type = connection.type || connection.effectiveType;
        if (type?.includes("wifi")) connectionType = "wifi";else if (type?.includes("cellular") || type?.includes("3g") || type?.includes("4g") || type?.includes("5g")) {
          connectionType = "cellular";
        } else if (type?.includes("ethernet")) connectionType = "ethernet";

        // Categorize speed based on effective bandwidth and connection type
        if (effectiveBandwidth >= 10 || connection.effectiveType === "4g") {
          speed = "fast";
        } else if (effectiveBandwidth >= 1.5 || connection.effectiveType === "3g") {
          speed = "medium";
        } else {
          speed = "slow";
        }

        // Override if user has data saver enabled
        if (saveData) {
          speed = "slow";
        }
      } else {
        // Fallback for Safari and other browsers without Network API
        try {
          connectionType = getConnectionTypeFromUA();
          effectiveBandwidth = await measureBandwidth();

          // Check for data saver preference (Safari doesn't expose this directly)
          // We'll use a heuristic: assume mobile users might prefer data saving
          // if (isMobileDevice() && connectionType === "cellular") {
          //   saveData = true
          // }

          // Categorize speed based on measured bandwidth and device type
          if (effectiveBandwidth >= 8) {
            speed = "fast";
          } else if (effectiveBandwidth >= 2) {
            speed = "medium";
          } else {
            speed = "slow";
          }

          // Be more conservative on mobile/cellular
          if (connectionType === "cellular" && speed === "fast") {
            speed = "medium";
          }
          if (saveData) {
            speed = "slow";
          }
        } catch {
          // Ultimate fallback: assume medium speed
          speed = "medium";
          connectionType = isMobileDevice() ? "cellular" : "unknown";
        }
      }
      setNetworkInfo({
        speed,
        connectionType,
        effectiveBandwidth,
        saveData
      });
    };

    // Initial check
    updateNetworkInfo();

    // Listen for online/offline changes (works in Safari)
    const handleOnline = () => updateNetworkInfo();
    const handleOffline = () => setNetworkInfo(prev_0 => ({
      ...prev_0,
      speed: "offline"
    }));
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Listen for connection changes if available
    const connection_0 = navigator.connection;
    if (connection_0) {
      connection_0.addEventListener("change", updateNetworkInfo);
    }
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (connection_0) {
        connection_0.removeEventListener("change", updateNetworkInfo);
      }
    };
  }, []);
  return networkInfo;
}